/**
 * EditorView Component - Replace Operation Implementation
 * 
 * Implements three-stage replace operation for word-level track changes:
 * 
 * STAGE 1: IDENTIFICATION
 * - Locks selection snapshot (text + OOXML) when user sends message
 * - Uses locked snapshot for all subsequent operations (comparison, apply)
 * - Optionally highlights selection in document for visual feedback
 * 
 * STAGE 2: COMPARISON
 * - Uses RobustXmlTrackChangesGenerator with locked original OOXML
 * - Generates word-level diff (w:ins, w:del) between original and suggested text
 * - Produces OOXML with precise track changes for panel display and document insertion
 * 
 * STAGE 3: PRESENTATION
 * - Panel: Shows track changes HTML (red strikethrough, blue underline) via TrackChangesParser
 * - Document: Tries insertOoxml to preserve word-level track changes in Word
 * - Fallback: Uses insertText if OOXML insertion fails
 * - Clears highlight and locked snapshot after successful replace
 */

import * as React from 'react';
import { Eye, Copy, Minus, MessageCircle, PencilLine } from 'lucide-react';
import { ChatService } from '../../features/chat/ChatService';
import { useSelection } from '../hooks/useSelection';
import { ChatComposer } from './ChatComposer';
import { TrackChangesParser } from '../../word/utils/TrackChangesParser';
import { RedlineInserter } from '../../word/trackChanges/RedlineInserter';
import { RobustXmlTrackChangesGenerator } from '../../features/rephrasing/RobustXmlTrackChangesGenerator';
import { handleError } from '../../utils/errorHandling';
import { detectTextDirection } from '../../utils/textUtils';
import './EditorView.css';
import { log, logError } from '@/utils/logging';
import { ProofreadWordAdapter } from '../../features/proofread/ProofreadWordAdapter';
import {
  getReviewPartyContextInstruction,
  hasReviewPartyContextComplete,
} from '../../utils/reviewPartyContext';
import { AskMarkdown } from './AskMarkdown';

interface EditorViewProps {
  chatService: ChatService;
  onRequireReview?: () => void;
}

interface EditorResult {
  processedText: string;
  originalText: string;
  originalOoxml: string; // Locked original OOXML snapshot
  editedOoxml: string; // OOXML with track changes
  trackChangesHtml: string; // HTML for display
  originalParagraphIndex?: number; // Paragraph index at send time for precise Replace
  /** What the user asked (composer or quick action), for the explanation panel */
  userRequest: string;
  /** Short model explanation tied to the request (from ChatQueryResult.response or polish copy) */
  explanation: string;
}

export const EditorView: React.FC<EditorViewProps> = ({ chatService, onRequireReview }) => {
  const selection = useSelection(chatService.getRephrasingService().getSelectionManager());
  const [result, setResult] = React.useState<EditorResult | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [streamingText, setStreamingText] = React.useState<string>(''); // For real-time streaming display
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [editedText, setEditedText] = React.useState<string>(''); // User-edited version of processed text
  const [draft, setDraft] = React.useState('');
  /** Snapshot of selection HTML/text at send time — shown until stream chunks replace it in place */
  const [streamLockedOriginal, setStreamLockedOriginal] = React.useState<{
    text: string;
    html: string | null;
    dir: 'ltr' | 'rtl';
  } | null>(null);
  const streamingTextRef = React.useRef<HTMLDivElement>(null);

  // Auto-scroll streaming text to bottom as content arrives
  React.useEffect(() => {
    if (isStreaming && streamingTextRef.current) {
      streamingTextRef.current.scrollTop = streamingTextRef.current.scrollHeight;
    }
  }, [isStreaming, streamingText]);

  // Debug: Log streaming state changes
  React.useEffect(() => {
    if (isStreaming) {
      console.log('[EditorView] Streaming state changed:', {
        isStreaming,
        streamingTextLength: streamingText.length,
        streamingTextPreview: streamingText.substring(0, 100)
      });
    }
  }, [isStreaming, streamingText]);

  // Debug: Log result state changes
  React.useEffect(() => {
    if (result) {
      console.log('[EditorView] Result state changed:', {
        hasResult: !!result,
        hasProcessedText: !!result.processedText,
        hasEditedOoxml: !!result.editedOoxml && result.editedOoxml.length > 0,
        hasTrackChangesHtml: !!result.trackChangesHtml && result.trackChangesHtml.length > 0,
        editedOoxmlLength: result.editedOoxml?.length || 0,
        trackChangesHtmlLength: result.trackChangesHtml?.length || 0,
        isStreaming,
        isProcessing,
        hasError: !!error
      });
    }
  }, [result, isStreaming, isProcessing, error]);

  // Check if Word.js is available
  const isWordAvailable = React.useMemo(() => {
    return typeof Word !== 'undefined' && typeof Word.run === 'function';
  }, []);

  // Create a stable key from OOXML by removing Word's auto-generated IDs
  // These IDs change on every refresh even when content is identical
  // Use a ref to cache normalized values to prevent unnecessary re-computation
  const normalizedCacheRef = React.useRef<Map<string, string>>(new Map());
  
  const ooxmlContentKey = React.useMemo(() => {
    const ooxml = selection?.ooxml;
    if (!ooxml || typeof ooxml !== 'string') return '';
    
    // Check cache first
    const cached = normalizedCacheRef.current.get(ooxml);
    if (cached !== undefined) {
      return cached;
    }
    
    // Remove Word-generated IDs that change on every refresh but don't affect content
    const normalized = ooxml
      .replace(/w14:paraId="[^"]*"/g, 'w14:paraId=""')
      .replace(/w14:textId="[^"]*"/g, 'w14:textId=""')
      .replace(/w:rsidR="[^"]*"/g, 'w:rsidR=""')
      .replace(/w:rsidRDefault="[^"]*"/g, 'w:rsidRDefault=""')
      .replace(/w:rsidRPr="[^"]*"/g, 'w:rsidRPr=""');
    
    // Cache the normalized result (limit cache size to prevent memory leaks)
    if (normalizedCacheRef.current.size > 10) {
      // Clear cache if it gets too large (keep only the most recent)
      const firstKey = normalizedCacheRef.current.keys().next().value;
      if (firstKey) {
        normalizedCacheRef.current.delete(firstKey);
      }
    }
    normalizedCacheRef.current.set(ooxml, normalized);
    
    return normalized;
  }, [selection?.ooxml]);

  // Memoize the HTML conversion to avoid re-computing on every render
  // Use normalized OOXML key to detect actual content changes
  const selectedTextHtml = React.useMemo(() => {
    if (!selection || !selection.ooxml || selection.ooxml.length === 0) {
      return null;
    }
    
    console.log('[EditorView] Converting OOXML to HTML (memoized)');
    console.log('[EditorView] OOXML length:', selection.ooxml.length);
    console.log('[EditorView] OOXML preview:', selection.ooxml.substring(0, 300));
    console.log('[EditorView] Selection has track changes:', selection.hasTrackChanges);
    
    const html = TrackChangesParser.convertOoxmlToHtml(selection.ooxml);
    console.log('[EditorView] Generated HTML length:', html.length);
    console.log('[EditorView] HTML contains track-change classes:', 
      html.includes('track-change-insertion') || html.includes('track-change-deletion'));
    
    return html;
  }, [ooxmlContentKey]); // Use normalized key to detect actual content changes

  // Detect text direction for the selected text
  const selectedTextDirection = React.useMemo(() => {
    if (!selection || selection.isEmpty) {
      return 'ltr';
    }
    // Use the plain text to detect direction
    return detectTextDirection(selection.text);
  }, [selection?.text]);

  // Live selection tracking only while Editor is visible — avoids a full-document
  // Word.run on every keystroke when the user is on Cross-Check or other tabs.
  React.useEffect(() => {
    if (!isWordAvailable) {
      return;
    }

    const rephrasingService = chatService.getRephrasingService();
    rephrasingService.startSelectionTracking();
    rephrasingService.refreshSelection().catch(() => {
      // Silently handle errors
    });

    return () => {
      rephrasingService.stopSelectionTracking();
    };
  }, [chatService, isWordAvailable]);

  // Do not apply any add-in highlight: desired state is Word's native selection only
  // (the "blue" in the reference screenshot is Word's selection, not a format we set)

  const handleSendMessage = async (messageText: string) => {
    if (!selection || selection.isEmpty) {
      setError('Please select some text in the document before sending a message.');
      return;
    }

    if (!hasReviewPartyContextComplete()) {
      onRequireReview?.();
      return;
    }

    // STAGE 1: IDENTIFICATION - Lock the selection snapshot at send time
    // This ensures we always compare against and apply to the same original text/OOXML
    // Locked snapshot is stored in result.originalText and result.originalOoxml for use in Replace
    const locked = {
      text: selection.text,
      ooxml: selection.ooxml || '',
      paragraphIndex: selection.paragraphIndex  // Index in body.paragraphs for precise paragraph selection
    };
    
    log('[EditorView] Locked selection snapshot:', {
      textLength: locked.text.length,
      //ooxmlLength: locked.ooxml.length,
      paragraphIndex: locked.paragraphIndex,
      textPreview: locked.text.substring(0, 100)
    });

    let lockedDisplayHtml: string | null = null;
    if (locked.ooxml && locked.ooxml.length > 0) {
      try {
        lockedDisplayHtml = TrackChangesParser.convertOoxmlToHtml(locked.ooxml);
      } catch {
        lockedDisplayHtml = null;
      }
    }
    setStreamLockedOriginal({
      text: locked.text,
      html: lockedDisplayHtml,
      dir: detectTextDirection(locked.text),
    });

    setIsProcessing(true);
    setIsStreaming(true);
    setError(null);
    setResult(null);
    setEditedText('');
    setStreamingText('');

    try {
      // Process the query with streaming
      log('[EditorView] Starting streaming query processing');
      let chunkCount = 0;
      let accumulatedText = ''; // Local accumulator like in ChatPage and ClauseCard
      
      // Use locked snapshot text for processing
      for await (const chunk of chatService.processQueryStream(messageText, locked.text)) {
        chunkCount++;
        log(`[EditorView] Received chunk #${chunkCount}:`, {
          type: chunk.type,
          hasContent: !!chunk.content,
          contentLength: chunk.content?.length || 0,
          hasResult: !!chunk.result,
          hasError: !!chunk.error
        });
        
        if (chunk.type === 'text' && chunk.content) {
          // Accumulate text chunks locally (like ChatPage and ClauseCard do)
          accumulatedText += chunk.content;
          log(`[EditorView] Accumulated text length: ${accumulatedText.length} chars`);
          
          // Update state with full accumulated text (forces React to re-render)
          // Use setTimeout to ensure React flushes the update immediately
          setStreamingText(accumulatedText);
          
          // Force a small delay to make streaming more visible (optional, can be removed)
          // This helps ensure React has time to render each chunk
          await new Promise(resolve => setTimeout(resolve, 0));
        } else if (chunk.type === 'complete' && chunk.result) {
          // Streaming complete - process final result
          const queryResult = chunk.result;
          log('[EditorView] Streaming complete, processing final result');
          
          setIsStreaming(false);
          
          if (queryResult.processedText) {
            log('[EditorView] Generating XML with track changes');
            log('[EditorView] Original OOXML available (locked):', !!locked.ooxml);
            log('[EditorView] Processed text length:', queryResult.processedText.length);

            // STAGE 2: COMPARISON - Generate XML with track changes using locked snapshot
            let editedOoxml = '';
            let trackChangesHtml = '';
            
            if (locked.ooxml && locked.ooxml.length > 0) {
              try {
                console.log('[EditorView] Starting XML generation with locked OOXML...', {
                  ooxmlLength: locked.ooxml.length,
                  processedTextLength: queryResult.processedText.length,
                  ooxmlPreview: locked.ooxml.substring(0, 200)
                });
                
                // Generate OOXML with track changes using locked original OOXML
                // Use 'AI Suggestion' as author so new suggestions get blue styling (track-change-new class)
                editedOoxml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
                  locked.ooxml,
                  queryResult.processedText,
                  'AI Suggestion'
                );
                
                console.log('[EditorView] Generated edited OOXML:', {
                  length: editedOoxml.length,
                  isEmpty: editedOoxml.length === 0,
                  preview: editedOoxml.substring(0, 200)
                });
                
                if (editedOoxml && editedOoxml.length > 0) {
                  // Convert to HTML for display
                  trackChangesHtml = TrackChangesParser.convertOoxmlToHtml(editedOoxml);
                  console.log('[EditorView] Generated track changes HTML:', {
                    length: trackChangesHtml.length,
                    isEmpty: trackChangesHtml.length === 0,
                    preview: trackChangesHtml.substring(0, 200),
                    hasTrackChanges: trackChangesHtml.includes('track-change')
                  });
                } else {
                  console.warn('[EditorView] Generated OOXML is empty, falling back to plain text');
                  trackChangesHtml = queryResult.processedText;
                }
              } catch (error) {
                console.error('[EditorView] Error generating track changes XML:', error);
                console.error('[EditorView] Error details:', {
                  message: error instanceof Error ? error.message : String(error),
                  stack: error instanceof Error ? error.stack : undefined
                });
                // Fallback: use plain text
                trackChangesHtml = queryResult.processedText;
              }
            } else {
              console.warn('[EditorView] No OOXML available, using plain text');
              trackChangesHtml = queryResult.processedText;
            }

            console.log('[EditorView] Setting result:', {
              hasProcessedText: !!queryResult.processedText,
              hasEditedOoxml: !!editedOoxml && editedOoxml.length > 0,
              hasTrackChangesHtml: !!trackChangesHtml && trackChangesHtml.length > 0,
              editedOoxmlLength: editedOoxml.length,
              trackChangesHtmlLength: trackChangesHtml.length
            });

            const explanation =
              (queryResult.response && queryResult.response.trim()) ||
              '**Note:** Suggested changes are shown above. Use **Replace** to apply them in the document.';

            // Set all state together to avoid intermediate renders
            // Store locked snapshot in result for use in Replace
            setResult({
              processedText: queryResult.processedText,
              originalText: locked.text,
              originalOoxml: locked.ooxml,
              editedOoxml: editedOoxml,
              trackChangesHtml: trackChangesHtml,
              originalParagraphIndex: locked.paragraphIndex,
              userRequest: messageText.trim(),
              explanation,
            });
            
            // Initialize edited text with processed text (user can edit it)
            setEditedText(queryResult.processedText);
            
            // Clear streaming text after setting final result
            setStreamingText('');
            setStreamLockedOriginal(null);

            // Ensure processing and streaming flags are cleared
            setIsProcessing(false);
            setIsStreaming(false);
            
            console.log('[EditorView] Result set, flags cleared:', {
              hasResult: true,
              isProcessing: false,
              isStreaming: false
            });
          } else {
            setError('No processed text returned from the service.');
            setIsProcessing(false);
            setIsStreaming(false);
            setStreamLockedOriginal(null);
          }
        } else if (chunk.type === 'error') {
          // Error during streaming
          setError(chunk.error || 'An error occurred during processing');
          setIsStreaming(false);
          setIsProcessing(false);
          setStreamingText('');
          setStreamLockedOriginal(null);
        }
      }
    } catch (err) {
      setError(handleError(err));
      setResult(null);
      setEditedText('');
      setIsStreaming(false);
      setIsProcessing(false);
      setStreamingText('');
      setStreamLockedOriginal(null);
    }
  };

  const handleReplace = async () => {
    if (!result) return;

    try {
      // Use edited text if user made changes, otherwise use original processed text
      const textToApply = editedText.trim() || result.processedText;
      
      // STAGE 3: PRESENTATION - Apply changes incrementally, word-by-word
      // Each word change is a separate Word API operation → word-level track changes
      await RedlineInserter.applyWordLevelChangesIncrementally(
        result.originalText,
        textToApply,
        result.trackChangesHtml,         // Drive deletions/insertions from pre-computed HTML
        result.originalParagraphIndex,   // Use paragraph index from locked snapshot
      );
      log('[EditorView] Text replacement completed successfully');

      // Keep the suggested edit visible after Replace so the user can reference or replace again
      setError(null);
    } catch (err) {
      logError('[EditorView] Error replacing text:', err);
      setError(`Failed to apply changes: ${handleError(err)}`);
    }
  };

  const handleCopy = () => {
    if (!result) return;
    const textToCopy = editedText.trim() || result.processedText;
    navigator.clipboard.writeText(textToCopy);
  };

  function handleNavigateToSelection() {
    if (!result) return;
    if (result.originalParagraphIndex != null && result.originalParagraphIndex >= 0) {
      void ProofreadWordAdapter.navigateToParagraph(result.originalParagraphIndex);
    } else {
      void ProofreadWordAdapter.navigateByContent(result.originalText);
    }
  }

  /** Dismiss suggested edit in the panel but keep the current Word selection */
  function handleDismissSuggestion() {
    setResult(null);
    setEditedText('');
    setError(null);
  }

  const handleQuickAction = (kind: 'favor' | 'mutual' | 'polish') => {
    if (kind === 'favor') {
      if (!getReviewPartyContextInstruction()) {
        setError('Complete the Review questions first so we know your party.');
        return;
      }
      void handleSendMessage('favor my party');
      return;
    }
    if (kind === 'mutual') {
      void handleSendMessage('make it mutual');
      return;
    }
    void handleSendMessage('polish');
  };

  function clearSelectionAndResult() {
    chatService.getRephrasingService().clearSelection();
    setResult(null);
    setEditedText('');
    setError(null);
    setStreamingText('');
    setIsStreaming(false);
    setIsProcessing(false);
    setStreamLockedOriginal(null);
  }

  const hasSelection = !!(selection && !selection.isEmpty);
  const showBusy = isStreaming || isProcessing;

  return (
    <div className="editor-view">
      {/* Single main canvas: selection OR streaming OR suggested edit (no duplicate blocks) */}
      <div className="editor-main-area">
        {error && (
          <div className="error-state editor-main-error" role="alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {showBusy && streamLockedOriginal ? (
          <div className="editor-panel editor-panel--streaming">
            <div className="editor-panel-header editor-panel-header--streaming">
              <div className="editor-panel-header-left">
                <span className="editor-panel-title">Selection</span>
                {isStreaming ? (
                  <span className="editor-generating-pill" aria-live="polite">
                    <span className="spinner spinner--inline" />
                    Generating…
                  </span>
                ) : (
                  <span className="editor-generating-pill">
                    <span className="spinner spinner--inline" />
                    Finishing…
                  </span>
                )}
              </div>
              <button
                type="button"
                className="editor-panel-dismiss"
                onClick={clearSelectionAndResult}
                title="Dismiss"
              >
                ✕
              </button>
            </div>
            <div
              ref={streamingTextRef}
              className="selected-text-full editor-panel-card editor-streaming-card"
              dir={
                streamingText.length > 0
                  ? detectTextDirection(streamingText)
                  : streamLockedOriginal.dir
              }
            >
              {streamingText.length > 0 ? (
                <div className="selected-text-plain editor-streaming-output">{streamingText}</div>
              ) : streamLockedOriginal.html ? (
                <div
                  className="selected-text-xml"
                  dangerouslySetInnerHTML={{ __html: streamLockedOriginal.html }}
                />
              ) : (
                <div className="selected-text-plain">{streamLockedOriginal.text}</div>
              )}
            </div>
          </div>
        ) : showBusy ? (
          <div className="processing-state">
            <div className="spinner" />
            <span>Processing…</span>
          </div>
        ) : result ? (
          <div className="editor-panel editor-panel--result">
            <div className="editor-panel-header">
              <span className="editor-panel-title">Suggested edit</span>
              <button
                type="button"
                className="editor-panel-dismiss"
                onClick={clearSelectionAndResult}
                title="Dismiss"
              >
                ✕
              </button>
            </div>
            {(() => {
              const hasTrackChangesHtml = result.trackChangesHtml && result.trackChangesHtml.length > 0;
              const hasEditedOoxml = result.editedOoxml && result.editedOoxml.length > 0;
              const shouldShowTrackChanges = hasTrackChangesHtml && hasEditedOoxml;

              return shouldShowTrackChanges ? (
                <div className="selected-text-full editor-panel-card">
                  <div
                    className="selected-text-xml"
                    dangerouslySetInnerHTML={{ __html: result.trackChangesHtml }}
                    dir={result.processedText ? detectTextDirection(result.processedText) : 'ltr'}
                  />
                </div>
              ) : (
                <div className="selected-text-full editor-panel-card">
                  <div className="selected-text-plain">{editedText || result.processedText}</div>
                </div>
              );
            })()}
            <div className="editor-suggested-actions">
              <button type="button" className="editor-replace-btn" onClick={handleReplace} title="Apply in document">
                Replace
              </button>
              <button
                type="button"
                className="editor-icon-btn"
                title="Navigate to paragraph"
                onClick={handleNavigateToSelection}
              >
                <Eye size={12} />
              </button>
              <button type="button" className="editor-icon-btn" title="Copy suggestion" onClick={handleCopy}>
                <Copy size={12} />
              </button>
              <button
                type="button"
                className="editor-icon-btn editor-ignore-btn"
                title="Dismiss suggestion"
                onClick={handleDismissSuggestion}
              >
                <Minus size={12} />
              </button>
            </div>
            <div className="editor-suggested-meta">
              <div
                className="editor-suggested-request"
                dir={detectTextDirection(result.userRequest)}
              >
                <span className="editor-suggested-explanation-label">Your request</span>
                <p className="editor-suggested-explanation-value" title={result.userRequest}>
                  {result.userRequest}
                </p>
              </div>
              <div className="editor-suggested-summary">
                <span className="editor-suggested-explanation-label">Summary</span>
                <div className="editor-suggested-summary-md">
                  <AskMarkdown
                    content={result.explanation}
                    dir={detectTextDirection(result.explanation)}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : hasSelection ? (
          <div className="editor-panel editor-panel--selection">
            <div className="editor-panel-header">
              <span className="editor-panel-title">Selection</span>
              <button
                type="button"
                className="editor-panel-dismiss"
                onClick={clearSelectionAndResult}
                title="Clear selection"
              >
                ✕
              </button>
            </div>
            <div className="selected-text-full editor-panel-card" dir={selectedTextDirection}>
              {selectedTextHtml ? (
                <div
                  className="selected-text-xml"
                  dangerouslySetInnerHTML={{ __html: selectedTextHtml }}
                />
              ) : (
                <div className="selected-text-plain">{selection!.text}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <MessageCircle className="empty-icon" size={48} />
            <p>Select text in the document to start editing</p>
            <p className="example-text">Use Polish, Make mutual, Favor my party, or type a request below</p>
          </div>
        )}
      </div>

      {/* Quick Actions - Near Chat Input */}
      <div className="quick-actions-bottom">
        <button 
          className="quick-action-btn"
          onClick={() => handleQuickAction('favor')}
          disabled={!selection || selection.isEmpty || isProcessing}
        >
          Favor my party
        </button>
        <button 
          className="quick-action-btn"
          onClick={() => handleQuickAction('mutual')}
          disabled={!selection || selection.isEmpty || isProcessing}
        >
          Make mutual
        </button>
        <button 
          className="quick-action-btn"
          onClick={() => handleQuickAction('polish')}
          disabled={!selection || selection.isEmpty || isProcessing}
        >
          Polish
        </button>
      </div>

      {/* Chat composer (same design as Agent / Ask) */}
      <div className="editor-chat-area">
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSubmit={async ({ text }) => {
            const t = text.trim();
            if (!t || isProcessing) return;
            if (!selection || selection.isEmpty) {
              setError('Please select some text in the document before sending a message.');
              return;
            }
            setDraft('');
            await handleSendMessage(t);
          }}
          disabled={isProcessing || !selection || selection.isEmpty}
          submitting={isProcessing}
          placeholder={
            !selection || selection.isEmpty
              ? 'Select text to start...'
              : 'Ask Courty'
          }
          layout="sticky"
          label={
            <div className="ag-composer-label">
              <PencilLine size={14} aria-hidden />
              <span>Edit selected text</span>
            </div>
          }
          followUp={!!result || isProcessing || isStreaming}
          submitTitle="Send edit request"
        />
      </div>
    </div>
  );
};

